import type { RouterOutputs } from 'shared/api'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { type TranslationKey, useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { INCOMING_PLACES, formatSum } from './remainders-format'
import { GoTo, LabelWithHint } from './remainders-parts'

export type PointRemaindersData = RouterOutputs['remainders']['AtPoint']
export type RemaindersParcels = NonNullable<PointRemaindersData['parcels']>

/**
 * A Point's remainders (ticket 47) — every number is the server's
 * (`remainders.AtPoint`); nothing here counts. Four sections, each a row of
 * figures under its title — the shelf, what is on its way, what the driver takes,
 * the cash — and the partners in one short table. A section's title links to the
 * section where its work is done, when the person sees that section (`opens`). A
 * part the role does not see is absent from the answer and so from the screen:
 * support sees no cash.
 */
export function PointRemainders({
	data,
	opens = () => true,
}: {
	data: PointRemaindersData
	/** Whether this person sees the section at `path`. */
	opens?: (path: string) => boolean
}) {
	const { t } = useTranslate()
	const p = data.parcels
	const cash = data.cash
	const hubShelfHint = p
		? [
				t('remainders.hubShelfHint'),
				...p.hubShelf.reasons.map((r) => `${t(`remainders.hubReason_${r.reason}` as TranslationKey)}: ${r.count}`),
			].join(' · ')
		: ''
	const go = (path: string, label: TranslationKey, testId: string) =>
		opens(path) ? <GoTo key={path} to={path} label={t(label)} testId={`remainders-link-${testId}`} /> : null

	return (
		<div className='flex flex-col gap-8' data-testid='remainders-point'>
			{p ? (
				<>
					<section className='flex flex-col gap-2' data-testid='remainders-shelf'>
						<SectionTitle
							title={t('remainders.shelfTitle')}
							aside={
								<>
									{go('/parcels', 'nav.parcels', 'parcels')}
									{go('/storage-expiry', 'nav.storageExpiry', 'storage-expiry')}
								</>
							}
						/>
						<Stats>
							<Stat label={t('remainders.shelf')} value={p.shelf.total} testId='remainders-stat-shelf' valueTestId='remainders-shelf-value' />
							<Stat
								label={t('remainders.expiringSoon')}
								value={p.shelf.expiringSoon}
								tone='warn'
								valueTestId='remainders-expiringSoon-value'
							/>
							<Stat
								label={<LabelWithHint label={t('remainders.awaitingDecision')} hint={t('remainders.awaitingDecisionHint')} />}
								value={p.shelf.awaitingDecision}
								tone='bad'
								valueTestId='remainders-awaitingDecision-value'
							/>
							<Stat label={t('remainders.toReturn')} value={p.shelf.toReturn} valueTestId='remainders-toReturn-value' />
							<Stat label={t('remainders.toDispose')} value={p.shelf.toDispose} valueTestId='remainders-toDispose-value' />
							<Stat
								label={t('remainders.unpaid')}
								value={p.unpaid.shelf.count}
								tone='warn'
								testId='remainders-stat-unpaid'
								valueTestId='remainders-unpaid-value'
							/>
						</Stats>
					</section>

					<section className='flex flex-col gap-2' data-testid='remainders-incoming'>
						<SectionTitle title={t('remainders.incoming')} aside={go('/point-bags', 'nav.pointBags', 'point-bags')} />
						<Stats>
							<Stat label={t('remainders.total')} value={p.incoming.total} valueTestId='remainders-incoming-value' />
							{INCOMING_PLACES.filter((place) => p.incoming.places[place] > 0).map((place) => (
								<Stat
									key={place}
									label={t(`remainders.place_${place}` as TranslationKey)}
									value={p.incoming.places[place]}
									testId={`remainders-place-${place}`}
								/>
							))}
							<Stat label={t('remainders.bagsOnTheWay')} value={p.incoming.bagsOnTheWay} valueTestId='remainders-bags-value' />
							<Stat
								label={<LabelWithHint label={t('remainders.hubShelf')} hint={hubShelfHint} />}
								value={p.hubShelf.total}
								tone='warn'
								testId='remainders-hub-shelf'
							/>
						</Stats>
					</section>

					<section className='flex flex-col gap-2' data-testid='remainders-pickup'>
						<SectionTitle
							title={t('remainders.pickup')}
							hint={t('remainders.pickupHint')}
							aside={go('/returns', 'nav.returns', 'returns')}
						/>
						<Stats>
							<Stat label={t('remainders.total')} value={p.pickup.total} valueTestId='remainders-pickup-value' />
							{p.pickup.reasons.map((r) => (
								<Stat
									key={r.reason}
									label={t(`remainders.pickupReason_${r.reason}` as TranslationKey)}
									value={r.count}
									testId={`remainders-pickup-${r.reason}`}
								/>
							))}
						</Stats>
					</section>
				</>
			) : null}

			{cash ? (
				<section className='flex flex-col gap-2' data-testid='remainders-cash'>
					<SectionTitle title={t('remainders.cash')} aside={go('/money/cash-bag', 'nav.cashBag', 'money')} />
					<Stats>
						<Stat label={t('remainders.cashInBox')} value={formatSum(cash.cash)} valueTestId='remainders-cash-value' />
						{cash.waiting.map((b) => (
							<Stat
								key={b.cashBagId}
								label={t('remainders.waitingBag', { code: b.code })}
								value={formatSum(b.countedCash)}
								testId={`remainders-waiting-${b.code}`}
							/>
						))}
						{p ? (
							<>
								<Stat
									label={t('remainders.unpaidOnShelfSum')}
									value={formatSum(p.unpaid.shelf.amount)}
									valueTestId='remainders-unpaid-shelf-sum'
								/>
								<Stat
									label={t('remainders.unpaidOnTheWaySum')}
									value={formatSum(p.unpaid.incoming.amount)}
									valueTestId='remainders-unpaid-incoming-sum'
								/>
							</>
						) : null}
					</Stats>
				</section>
			) : null}

			{p ? <ByPartner parcels={p} cash={cash ?? null} /> : null}
		</div>
	)
}

type PartnerRow = {
	key: string
	name: string
	shelf: number
	incoming: number
	unpaid: number
	unpaidSum: number
	cash: number
}

/**
 * The Point by partner — the shelf, what is on its way, the unpaid and the cash of
 * each, in one table: the server's per-partner lines put side by side, nothing summed.
 */
function ByPartner({ parcels: p, cash }: { parcels: RemaindersParcels; cash: PointRemaindersData['cash'] | null }) {
	const { t } = useTranslate()
	const rows = new Map<string, PartnerRow>()
	const row = (partnerId: string | null, partnerName: string) => {
		const key = partnerId ?? 'none'
		let r = rows.get(key)
		if (!r) {
			r = { key, name: partnerName || t('remainders.noPartner'), shelf: 0, incoming: 0, unpaid: 0, unpaidSum: 0, cash: 0 }
			rows.set(key, r)
		}
		return r
	}
	for (const s of p.shelf.partners) row(s.partnerId, s.partnerName).shelf = s.count
	for (const s of p.incoming.partners) row(s.partnerId, s.partnerName).incoming = s.count
	for (const s of p.unpaid.shelf.partners) {
		const r = row(s.partnerId, s.partnerName)
		r.unpaid = s.count
		r.unpaidSum = s.amount
	}
	for (const s of cash?.partners ?? []) row(s.partnerId, s.partnerName).cash = s.cash
	const list = [...rows.values()]
	const num = 'text-right tabular-nums'
	const quiet = (n: number) => cn(num, n === 0 && 'text-muted-foreground')
	const columns = cash ? 6 : 4

	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('remainders.byPartner')} count={list.length} />
			<CompactTable testId='remainders-by-partner'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('remainders.colPartner')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colShelf')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colIncoming')}</TableHead>
						<TableHead className='text-right'>{t('remainders.colUnpaid')}</TableHead>
						{cash ? (
							<>
								<TableHead className='text-right'>{t('remainders.colUnpaidSum')}</TableHead>
								<TableHead className='text-right'>{t('remainders.colCash')}</TableHead>
							</>
						) : null}
					</TableRow>
				</TableHeader>
				{list.length === 0 ? (
					<EmptyTableBody columns={columns}>{t('remainders.byPartnerEmpty')}</EmptyTableBody>
				) : (
					<TableBody>
						{list.map((r) => (
							<TableRow key={r.key} data-testid={`remainders-partner-${r.key}`}>
								<TableCell className='font-medium'>{r.name}</TableCell>
								<TableCell className={quiet(r.shelf)}>{r.shelf}</TableCell>
								<TableCell className={quiet(r.incoming)}>{r.incoming}</TableCell>
								<TableCell className={quiet(r.unpaid)}>{r.unpaid}</TableCell>
								{cash ? (
									<>
										<TableCell className={quiet(r.unpaidSum)}>{formatSum(r.unpaidSum)}</TableCell>
										<TableCell className={quiet(r.cash)}>{formatSum(r.cash)}</TableCell>
									</>
								) : null}
							</TableRow>
						))}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}
